import {
  getChargeRate,
  getBillRateShiftPaymentSummary,
  isBillRateShift,
} from 'lib/payment'
import { CalculatorIcon } from 'lucide-react'

import styled from 'styled-components'
import { Flex, Box, DisplayIcon, Heading, Text } from 'ui'
import { useShiftDetail } from '../useShiftDetail'
import { formatCurrency } from 'lib/string'
import { useAuthContext } from 'components/auth'

const LineItem = styled(Flex).attrs({
  px: 3,
  fontSize: 1,
})``

const ChargeEstimateHeader = () => (
  <Box display={'inline-flex'} alignItems={'center'}>
    <DisplayIcon>
      <CalculatorIcon size={16} />
    </DisplayIcon>
    <Heading level={4} my={0} ml={2}>
      Charge Estimate
    </Heading>
  </Box>
)

const MarkupPaymentSummary = ({
  totalEstimatedCharge,
  chargeRate,
}: {
  totalEstimatedCharge: number
  chargeRate: number
}) => {
  const subtotal = totalEstimatedCharge / chargeRate
  const bookingFees = totalEstimatedCharge - subtotal

  return (
    <Box mt={3}>
      <LineItem>
        <Box flex={1}>Worker Pay</Box>
        {formatCurrency(subtotal)}
      </LineItem>
      <LineItem>
        <Box flex={1}>
          WorkWhile Fee ({((chargeRate - 1) * 100).toPrecision(2)}%)
        </Box>{' '}
        {formatCurrency(bookingFees)}
      </LineItem>
      <hr />
      <LineItem>
        <Box flex={1}>Total</Box>
        <Text fontWeight={1} fontSize={3} color={'info'}>
          {formatCurrency(totalEstimatedCharge)}
        </Text>
      </LineItem>
    </Box>
  )
}

const BillRatePaymentSummary = ({
  summary,
}: {
  summary: NonNullable<ReturnType<typeof getBillRateShiftPaymentSummary>>
}) => (
  <Box mt={3}>
    <Text fontWeight={2} mb={2}>
      Base Charges
    </Text>
    <LineItem>
      <Box flex={1}>Workers</Box>
      {summary.numWorkers}
    </LineItem>
    <LineItem>
      <Box flex={1}>Total Shift Hours</Box>
      {summary.totalShiftHours}
    </LineItem>
    <LineItem>
      <Box flex={1}>Bill Rate Per Hour</Box>
      {formatCurrency(summary.billRate)}
    </LineItem>
    <hr />
    <LineItem>
      <Box flex={1} />
      <Text fontWeight={2}>{formatCurrency(summary.baseCharge)}</Text>
    </LineItem>
    {summary.bonusPerWorker ? (
      <>
        <Text fontWeight={2} mb={2} mt={3}>
          Estimated Bonus
        </Text>
        <LineItem>
          <Box flex={1}>Bonus per Worker</Box>
          {formatCurrency(summary.bonusPerWorker)}&nbsp;x&nbsp;
          {summary.numWorkers}
        </LineItem>
        <LineItem>
          <Box flex={1}>WorkWhile Fee ({summary.bonusWWFeePercent}%)</Box>
          {formatCurrency(summary.bonusWWFee)}
        </LineItem>
        <hr />
        <LineItem>
          <Box flex={1} />
          <Text fontWeight={2}>{formatCurrency(summary.totalBonus)}</Text>
        </LineItem>
      </>
    ) : null}
    <Text fontWeight={2} mb={2} mt={3}>
      Total
    </Text>
    <LineItem>
      <Box flex={1}>Total Charge</Box>
      <Text fontWeight={1} fontSize={3} color={'info'}>
        {formatCurrency(summary.total)}
      </Text>
    </LineItem>
  </Box>
)

export const ShiftDetailPaymentSummary = () => {
  const { company } = useAuthContext()
  const { shift } = useShiftDetail()

  if (!shift || !company) return null

  if (isBillRateShift(shift)) {
    const summary = getBillRateShiftPaymentSummary({
      shift,
      wwFeePercent: company.takePercentageEstimate,
    })
    if (!summary) return null

    return (
      <Box
        p={3}
        backgroundColor={'neutrals.50'}
        borderRadius={'large'}
        color={'neutrals.400'}
      >
        <ChargeEstimateHeader />
        <BillRatePaymentSummary summary={summary} />
      </Box>
    )
  }

  const chargeRate = getChargeRate(company)
  const totalEstimatedCharge = shift.totalEstimatedCharge || 0

  return (
    <Box
      p={3}
      backgroundColor={'neutrals.50'}
      borderRadius={'large'}
      color={'neutrals.400'}
    >
      <ChargeEstimateHeader />
      <MarkupPaymentSummary
        totalEstimatedCharge={totalEstimatedCharge}
        chargeRate={chargeRate}
      />
    </Box>
  )
}
